(function initialize() {
  if (customElements.get('tm-bundle-builder')) return;
  const needsProvider = () => [...document.querySelectorAll('[data-tm-bb-campaign-configuration]')].some(node => {
    try { return JSON.parse(node.textContent).enabled === true; } catch (_) { return false; }
  });
  const dependenciesReady = () => window.TheManBundleState && window.TheManPaidCart && (!needsProvider() || window.TheManBundleProviderFactory);
  if (!dependenciesReady()) {
    // Deferred and Theme Editor script loads may arrive in any order.
    const retry = () => {
      if (!dependenciesReady()) return;
      document.removeEventListener('load', retry, true); initialize();
    };
    document.addEventListener('load', retry, true);
    return;
  }
  // Only the offline review generator supplies this marker and rejecting adapters.
  // No theme template/config emits it; storefront origins cannot enter preview.
  const localPreview = () => ['file:', 'http:'].includes(location.protocol) &&
    (location.protocol === 'file:' || ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname)) &&
    document.documentElement.dataset.tmLocalPreview === 'THEMAN-002' &&
    window.TheManBundlePreview?.version === 1;
  const sessions = new Map();
  const money = (amount, currency, locale) => new Intl.NumberFormat(locale || 'en-PH', { style: 'currency', currency }).format(amount / 100);
  const cartHasVariants = (cart, ids) => Array.isArray(cart?.items) && ids.every(id =>
    cart.items.some(item => String(item.variant_id || item.id) === String(id) && item.quantity > 0));
  const node = (tag, text, className) => {
    const el = document.createElement(tag);
    if (text != null) el.textContent = text;
    if (className) el.className = className;
    return el;
  };
  const overlayActive = () => document.documentElement.matches('.js-drawer-open,.js-drawer-closing,.js-modal-open') ||
    !!document.querySelector('dialog[open],.drawer--is-open,.modal--is-active');
  class DialogSurface extends HTMLElement {
    bindRecovery(signal) {
      if (localPreview()) return;
      const update = state => {
        this.uncertain = state.blocked;
        this.querySelectorAll('[data-cart-recheck]').forEach(button => { button.hidden = !state.blocked; });
        if (this.cards) {
          this.cards.forEach(card => { card.uncertain = state.blocked; });
          this.refreshSets();
          if (state.blocked && state.message) this.querySelectorAll('[data-set-status]').forEach(el => { el.textContent = state.message; });
        } else {
          this.render();
          if (state.blocked && state.message) this.status(state.message);
          else if (state.confirmed) this.status(this.t(this.nativePaid ? 'native_paid' : 'repeat_notice'));
        }
      };
      document.addEventListener('tm:paid-cart-state', event => update(event.detail), { signal });
      this.addEventListener('click', event => { if (event.target.closest('[data-cart-recheck]')) window.TheManPaidCart.recover(); }, { signal });
      const saved = window.TheManPaidCart.status(); update(saved);
      if (saved.blocked) window.TheManPaidCart.recover();
    }
    bindDialogs(signal) {
      this.addEventListener('click', event => {
        const opener = event.target.closest('[data-dialog-open]');
        if (opener) this.openDialog(document.getElementById(opener.dataset.dialogOpen), opener);
        const closer = event.target.closest('[data-dialog-close]');
        if (closer) closer.closest('dialog').close();
      }, { signal });
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('close', () => {
          if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('tm-bb-dialog-open');
          if (dialog.returnTarget?.isConnected && !overlayActive()) dialog.returnTarget.focus({ preventScroll: true });
          this.syncSticky?.();
        }, { signal });
        dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); }, { signal });
      });
      document.addEventListener('drawerOpen', () => this.querySelectorAll('dialog[open]').forEach(d => d.close()), { signal });
    }
    openDialog(dialog, opener) {
      if (!dialog || overlayActive()) return;
      dialog.returnTarget = opener;
      dialog.showModal();
      document.documentElement.classList.add('tm-bb-dialog-open');
      dialog.querySelector('.tm-bb-dialog-body').scrollTop = 0;
      dialog.querySelector('[data-dialog-close]').focus({ preventScroll: true });
      this.syncSticky?.();
    }
    disconnectDialogs() {
      this.querySelectorAll('dialog[open]').forEach(d => d.close());
      if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('tm-bb-dialog-open');
    }
  }
  class BundleBuilder extends DialogSurface {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.pending = false;
      const { signal } = this.controller;
      this.state = window.TheManBundleState;
      if (!this.state) return;
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.catalog = new Map();
      this.products = new Map();
      this.querySelectorAll('[data-product-json]').forEach(script => {
        const product = JSON.parse(script.textContent);
        this.products.set(product.id, product);
        product.variants.forEach(v => this.catalog.set(v.id, { ...v, product: product.id, name: product.displayName || product.title }));
      });
      this.preview = localPreview();
      this.storageKey = `${this.preview ? 'tm-bb-preview' : 'tm-bb'}:${location.pathname}:${this.dataset.section}`;
      let saved = sessions.get(this.storageKey);
      try { saved ||= JSON.parse(sessionStorage.getItem(this.storageKey)); } catch (_) {}
      this.draft = this.state.restore(saved, this.catalog, this.dataset.currency);
      this.picks = saved?.picks && typeof saved.picks === 'object' && !Array.isArray(saved.picks) ? saved.picks : {};
      this.products.forEach(product => {
        if (!product.variants.some(v => v.id === this.picks[product.id]?.id)) {
          const first = product.variants.find(v => v.available) || product.variants[0];
          this.picks[product.id] = { id: first?.id, quantity: first?.rule?.min || 1 };
        }
        if (!Number.isSafeInteger(this.picks[product.id].quantity) || this.picks[product.id].quantity < 1) this.picks[product.id].quantity = 1;
        this.syncProduct(product.id);
      });
      this.bindDialogs(signal);
      this.addEventListener('submit', event => {
        if (!event.target.matches('[data-draft-form]')) return;
        event.preventDefault();
        this.addDraft(event.target);
      }, { signal });
      this.addEventListener('change', event => {
        const form = event.target.closest('[data-draft-form]');
        if (!form) return;
        const pick = this.picks[form.dataset.product];
        if (event.target.matches('[data-variant]')) {
          pick.id = event.target.value;
          pick.quantity = this.catalog.get(pick.id)?.rule?.min || 1;
        } else if (event.target.matches('[data-quantity]')) pick.quantity = Number(event.target.value);
        this.syncProduct(form.dataset.product);
        this.persist();
      }, { signal });
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-tab]');
        if (tab) this.activateTab(tab.dataset.tab);
        const remove = event.target.closest('[data-remove]');
        if (remove) this.changeDraft(remove.dataset.remove, 0, remove);
        const change = event.target.closest('[data-adjust]');
        if (change) this.changeDraft(change.dataset.id, this.draft[change.dataset.id] + Number(change.dataset.adjust), change);
        if (event.target.closest('[data-review]')) {
          this.renderReview();
          this.openDialog(this.querySelector('[data-review-dialog]'), event.target.closest('[data-review]'));
          if (this.campaignProvider && Object.keys(this.draft).length && !this.pending) this.prepareCampaign();
        }
        const scroll = event.target.closest('[data-scroll]');
        if (scroll) this.querySelector('[data-holder]').scrollBy({ left: Number(scroll.dataset.scroll) * 260, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        if (event.target.closest('[data-transfer]')) this.transfer();
      }, { signal });
      this.addEventListener('keydown', event => {
        const tab = event.target.closest('[data-tab]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const tabs = [...this.querySelectorAll('[data-tab]')];
        let index = tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1);
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = tabs.length - 1;
        const next = tabs[(index + tabs.length) % tabs.length];
        this.activateTab(next.dataset.tab); next.focus();
      }, { signal });
      this.visibility = new IntersectionObserver(() => this.syncSticky(), { threshold: 0 });
      [this.querySelector('[data-purchase-area]'), this.querySelector('[data-catalog]')].forEach(el => this.visibility.observe(el));
      this.overlayObserver = new MutationObserver(() => this.syncSticky());
      this.overlayObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      this.overlayObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'class'] });
      document.addEventListener('cart:open', () => this.suppressSticky(), { signal });
      const cartUpdated = event => {
        if (!this.lastAddedIds || cartHasVariants(event.detail?.cart, this.lastAddedIds)) return;
        if (!Array.isArray(event.detail?.cart?.items)) return;
        this.lastAddedIds = null;
        if (!this.uncertain) this.status(this.t(this.nativePaid ? 'native_paid' : 'repeat_notice'));
      };
      document.addEventListener('cart:updated', cartUpdated, { signal });
      document.addEventListener('tm:cart-state-updated', cartUpdated, { signal });
      document.addEventListener('drawerOpen', () => this.suppressSticky(), { signal });
      document.addEventListener('click', event => { if (event.target.closest('.js-drawer-open-cart')) this.suppressSticky(); }, { capture: true, signal });
      document.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-panel]') || event.target.querySelector?.('[data-panel]');
        if (panel && this.contains(panel)) this.activateTab(panel.dataset.panel);
      }, { signal });
      window.addEventListener('resize', () => this.syncSticky(), { passive: true, signal });
      window.addEventListener('scroll', () => this.syncSticky(), { passive: true, signal });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refreshQuote(); }, { signal });
      document.addEventListener('tm:offers-ready', () => this.refreshQuote(), { signal });
      window.addEventListener('focus', () => this.refreshQuote(), { signal });
      if (!this.preview) this.bindCampaign(signal);
      this.render();
      this.refreshQuote();
      if (!this.campaignProvider) this.bindRecovery(signal);
    }
    disconnectedCallback() {
      this.persist?.();
      this.disconnectDialogs();
      this.controller?.abort(); this.controller = null;
      this.clearCampaignReview();
      this.visibility?.disconnect(); this.overlayObserver?.disconnect();
      clearTimeout(this.quoteTimer);
      this.quoteSequence = (this.quoteSequence || 0) + 1;
    }
    t(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`__${name.toUpperCase()}__`, value), this.labels[key] || key);
    }
    format(amount) { return money(amount, this.dataset.currency, this.dataset.locale); }
    persist() {
      if (!this.draft) return;
      const saved = { version: 1, draft: this.draft, picks: this.picks, currency: this.dataset.currency };
      sessions.set(this.storageKey, saved);
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(saved)); } catch (_) {}
    }
    activateTab(id) {
      this.querySelectorAll('[data-tab]').forEach(tab => { const active = tab.dataset.tab === id; tab.setAttribute('aria-selected', active); tab.tabIndex = active ? 0 : -1; });
      this.querySelectorAll('[data-panel]').forEach(panel => { panel.hidden = panel.dataset.panel !== id; });
    }
    syncProduct(id) {
      const pick = this.picks[id], variant = this.catalog.get(pick?.id);
      if (!variant) return;
      this.querySelectorAll(`[data-product-card="${id}"]`).forEach(card => {
        card.querySelectorAll('[data-variant]').forEach(select => { select.value = pick.id; });
        card.querySelectorAll('[data-quantity]').forEach(input => {
          input.value = pick.quantity; input.min = variant.rule?.min || 1; input.step = variant.rule?.increment || 1;
          const limits = [variant.rule?.max, variant.stock].filter(v => v != null);
          if (limits.length) input.max = Math.min(...limits); else input.removeAttribute('max');
        });
        card.querySelectorAll('[data-price]').forEach(el => { el.textContent = this.format(variant.price); });
        card.querySelectorAll('[data-add-draft]').forEach(button => { button.disabled = !variant.available; button.textContent = this.t(variant.available ? 'add_draft' : 'sold_out'); });
        card.querySelectorAll('[data-variant-image]').forEach(image => {
          image.hidden = !variant.image;
          if (variant.image) { image.src = variant.image; image.removeAttribute('srcset'); image.alt = variant.name; }
        });
        card.querySelectorAll('[data-photo-caption]').forEach(el => {
          const known = card.dataset.referenceSize && String(variant.imageId) === card.dataset.referenceImage;
          el.textContent = this.t(known ? 'photo_known' : 'photo_reference', { reference: card.dataset.referenceSize, size: variant.title });
        });
        card.querySelectorAll('[data-feedback]').forEach(el => { el.textContent = ''; });
      });
    }
    addDraft(form) {
      const productId = form.dataset.product;
      const pick = this.picks[productId];
      const variantId = form.querySelector('[data-variant]')?.value;
      const variant = this.catalog.get(variantId);
      const quantityValue = form.querySelector('[data-quantity]')?.value;
      const quantity = Number(quantityValue);
      const total = (this.draft[variantId] || 0) + quantity;
      if (this.pending) return;
      if (!pick || !variant || String(variant.product) !== productId || quantityValue === '' ||
          !this.state.validateQuantity(variant, quantity) || !this.state.validateQuantity(variant, total)) {
        form.querySelector('[data-feedback]').textContent = this.t('quantity_error'); return;
      }
      pick.id = variantId; pick.quantity = quantity;
      this.draft[variantId] = total;
      this.quote = null;
      this.syncProduct(productId);
      this.persist(); this.render(); this.refreshQuote();
      form.querySelector('[data-feedback]').textContent = this.t('draft_added', { quantity });
    }
    changeDraft(id, quantity, trigger) {
      if (this.pending) return;
      if (quantity < 0 || (quantity && !this.state.validateQuantity(this.catalog.get(id), quantity))) {
        this.status(this.t('quantity_error')); return;
      }
      const dialog = this.querySelector('[data-review-dialog]');
      const body = dialog.querySelector('.tm-bb-dialog-body');
      const scroll = body.scrollTop;
      const oldRows = [...this.querySelectorAll('[data-review-row]')];
      const rowIndex = oldRows.indexOf(trigger.closest('[data-review-row]'));
      const adjust = trigger.dataset.adjust;
      if (quantity === 0) delete this.draft[id]; else this.draft[id] = quantity;
      this.quote = null; this.persist(); this.render();
      body.scrollTop = scroll;
      if (dialog.open) {
        const rows = [...dialog.querySelectorAll('[data-review-row]')];
        const target = quantity ? dialog.querySelector(`[data-review-row="${id}"] [data-adjust="${adjust}"]`) : rows[Math.min(Math.max(0, rowIndex), rows.length - 1)]?.querySelector('button');
        (target || dialog.querySelector('[data-dialog-close]')).focus({ preventScroll: true });
      } else {
        const next = this.querySelector(`[data-remove="${id}"]`) || this.querySelector('[data-holder]');
        next.focus({ preventScroll: true });
      }
      this.refreshQuote();
    }
    status(message) { this.querySelectorAll('[data-transfer-status]').forEach(el => { el.textContent = message; }); }
    render() {
      const summary = this.state.summary(this.draft, this.catalog);
      this.presentation = this.preview ? window.TheManBundlePreview.evaluate(summary) : null;
      const campaignActive = Boolean(this.campaignProvider && this.campaignVersionMatches() && this.campaignProvider.isActiveHere());
      const quote = this.preview ? this.presentation : campaignActive ?
        (this.prepared || this.campaignEstimate || this.campaignMilestones) : this.campaignProvider ? null : this.quote;
      const campaignEstimate = Boolean(this.campaignProvider && quote && Number.isSafeInteger(quote.estimatedBagTotal));
      const campaignPending = Boolean(this.campaignProvider && !campaignEstimate);
      const gifts = quote?.gifts || [];
      const giftCount = gifts.reduce((n, g) => n + g.quantity, 0);
      this.querySelectorAll('[data-count]').forEach(el => { el.textContent = (this.preview || campaignEstimate) ? this.t('sample_count', { count: summary.count, samples: giftCount }) : this.t('count', { count: summary.count, gifts: giftCount }); });
      this.querySelectorAll('[data-total]').forEach(el => { el.textContent = campaignPending ? '—' : this.format(campaignEstimate ? quote.incrementalBagCharge : quote?.total ?? summary.subtotal); });
      this.querySelectorAll('[data-savings]').forEach(el => { el.textContent = quote?.discount ? `${this.t(campaignEstimate ? 'campaign_selection_savings' : this.preview ? 'fragrance_savings' : 'save')} ${this.format(quote.discount)}` : ''; });
      this.querySelectorAll('[data-total-caption]').forEach(el => { el.hidden = !(this.preview || this.campaignProvider); el.textContent = campaignEstimate ? this.t('campaign_bag_change') : (this.preview || this.campaignProvider) ? this.t('estimated_total') : ''; });
      this.querySelectorAll('[data-total-label]').forEach(el => { el.textContent = this.t(campaignEstimate ? 'campaign_bag_change' : (this.preview || this.campaignProvider) ? 'estimated_total' : 'total'); });
      const holder = this.querySelector('[data-holder]');
      const scroll = holder.scrollLeft;
      holder.replaceChildren();
      for (const line of summary.lines) holder.append(this.itemTile(line));
      for (const gift of gifts) for (let i = 0; i < gift.quantity; i++) holder.append(this.giftTile(gift));
      for (const gift of this.preview || this.campaignProvider ? quote?.upcomingGifts || [] : []) for (let i = 0; i < gift.quantity; i++) holder.append(this.giftTile(gift, true));
      if (quote?.shippingQualified && quote.shippingMessage) { const shipping = node('article', null, 'tm-bb-tile tm-bb-gift'); shipping.append(node('strong', quote.shippingMessage)); holder.append(shipping); }
      if (!summary.count) {
        for (let i = 0; i < 3; i++) { const tile = node('div', null, 'tm-bb-tile tm-bb-starter'); tile.append(node('span', '+'), node('small', this.t('empty_slot'))); holder.append(tile); }
      }
      holder.scrollLeft = scroll;
      const rail = this.querySelector('[data-rail]'); rail.replaceChildren(); rail.hidden = !quote?.tiers?.length;
      quote?.tiers?.forEach(tier => {
        const li = node('li'); li.dataset.achieved = (quote.eligibleCount ?? summary.count) >= tier.min;
        const dot = node('span', null, 'tm-bb-milestone');
        if ((quote.eligibleCount ?? summary.count) >= tier.min && this.querySelector('[data-logo]')) dot.append(this.querySelector('[data-logo]').content.cloneNode(true));
        else dot.textContent = tier.min;
        li.append(dot, node('span', this.t('buy', { count: tier.min })), node('strong', this.t('percent_off', { percent: tier.bps / 100 })));
        rail.append(li);
      });
      let progressText = this.t(summary.count ? 'review_prompt' : 'start');
      if (this.campaignProvider && !summary.count && quote === this.campaignMilestones && quote?.next) {
        progressText += ` ${this.t('next_tier_many', { remaining: quote.next.min, percent: quote.next.bps / 100 })}`;
        const upcoming = quote.upcomingGifts || [];
        if (upcoming.length === 1 && upcoming[0].quantity > 0)
          progressText += ` ${this.t(upcoming[0].quantity === 1 ? 'next_sample_one' : 'next_sample_many',
            { samples: upcoming[0].quantity, price: this.format(upcoming[0].unitCharge) })}`;
      }
      if (typeof quote?.progress === 'string') progressText = quote.progress;
      else if (quote?.progress) {
        const progress = quote.progress;
        progressText = this.t('sample_entitlement', { total: progress.earnedSamples, added: giftCount });
        const remaining = progress.nextMin - progress.eligibleCount;
        const nextTier = quote.tiers?.find(tier => tier.min === progress.nextMin);
        if (Number.isSafeInteger(remaining) && remaining > 0 && nextTier) {
          progressText += ` ${this.t(remaining === 1 ? 'next_tier_one' : 'next_tier_many', { remaining, percent: nextTier.bps / 100 })}`;
          const upcoming = quote.upcomingGifts || [];
          const newSamples = upcoming.reduce((count, gift) => count + gift.quantity, 0);
          if (newSamples > 0 && upcoming.every(gift => Number.isSafeInteger(gift.unitCharge) && gift.unitCharge >= 0))
            progressText += ` ${this.t(newSamples === 1 ? 'next_sample_one' : 'next_sample_many', { samples: newSamples, price: this.format(upcoming[0].unitCharge) })}`;
        } else if (progress.nextMin == null && quote.tiers?.length) {
          progressText += ` ${this.t('top_tier', { percent: quote.tiers.at(-1).bps / 100 })}`;
        }
      }
      this.querySelector('[data-progress]').textContent = progressText;
      this.querySelectorAll('[data-shipping]').forEach(el => { el.hidden = !quote?.shippingMessage; el.textContent = quote?.shippingMessage || ''; });
      this.querySelectorAll('[data-transfer]').forEach(button => {
        button.disabled = this.preview || this.pending || this.uncertain || !summary.count || !(campaignActive || this.nativePaid || quote?.purchasable);
        if (this.campaignProvider) button.textContent = this.t(button.closest('[data-review-dialog]') && this.prepared ? 'add_bundle' : 'review');
      });
      if (this.campaignChoice) this.campaignChoice.disabled = this.pending;
      if (this.campaignConsent) this.campaignConsent.hidden = !summary.count;
      if (this.campaignSummary) this.campaignSummary.hidden = !this.prepared;
      this.renderReview(); this.syncSticky();
    }
    itemTile(line) {
      const tile = node('article', null, 'tm-bb-tile');
      const remove = node('button', '×', 'tm-bb-remove'); remove.type = 'button'; remove.dataset.remove = line.id; remove.setAttribute('aria-label', this.t('remove', { name: `${line.variant.name} ${line.variant.title}` }));
      if (line.variant.image) { const image = node('img'); image.src = line.variant.image; image.alt = ''; image.width = 96; image.height = 112; image.loading = 'lazy'; tile.append(image); }
      tile.append(remove, node('strong', line.variant.name), node('small', `${line.variant.title} · ×${line.quantity}`)); return tile;
    }
    giftTile(gift, upcoming = false) {
      const tile = node('article', null, 'tm-bb-tile tm-bb-gift');
      if (gift.image) { const image = node('img'); image.src = gift.image; image.alt = ''; image.width = 96; image.height = 112; tile.append(image); }
      tile.dataset.upcoming = upcoming;
      tile.append(node('strong', this.campaignProvider ? this.t('sample_tile') : gift.title));
      if (!this.campaignProvider) tile.append(node('small', gift.size));
      // Present only the disclosed estimate; neither an entitlement nor a tile
      // means a sample has already been added to the shopper's bag.
      if (Number.isSafeInteger(gift.unitCharge) && gift.unitCharge >= 0) tile.append(node('small', this.t(upcoming ? 'sample_upcoming_each' : 'sample_each', { price: this.format(gift.unitCharge) })));
      return tile;
    }
    renderReview() {
      const container = this.querySelector('[data-review-items]'); if (!container) return;
      const scroll = container.parentElement.scrollTop;
      const focused = document.activeElement;
      const focusedId = focused?.dataset.id;
      const focusedAdjust = focused?.dataset.adjust;
      container.replaceChildren();
      const summary = this.state.summary(this.draft, this.catalog);
      for (const line of summary.lines) {
        const row = node('div', null, 'tm-bb-review-row'); row.dataset.reviewRow = line.id;
        const text = node('div'); text.append(node('strong', line.variant.name), node('small', line.variant.title), node('span', this.format(line.variant.price * line.quantity)));
        const controls = node('div', null, 'tm-bb-review-quantity');
        const increment = line.variant.rule?.increment || 1;
        for (const [symbol, delta, label] of [['−', -increment, 'decrease'], ['+', increment, 'increase']]) {
          const button = node('button', symbol); button.type = 'button'; button.dataset.adjust = delta; button.dataset.id = line.id; button.setAttribute('aria-label', this.t(label, { name: line.variant.name }));
          controls.append(button);
          if (delta < 0) controls.append(node('span', line.quantity));
        }
        const remove = node('button', '×', 'tm-bb-review-remove'); remove.type = 'button'; remove.dataset.remove = line.id; remove.setAttribute('aria-label', this.t('remove', { name: line.variant.name }));
        row.append(text, controls, remove); container.append(row);
      }
      if (!summary.count) container.append(node('p', this.t('empty')));
      const offer = this.preview ? this.presentation : this.campaignProvider ?
        (this.campaignProvider.isActiveHere() ? this.prepared || this.campaignEstimate : null) : this.quote;
      for (const gift of offer?.gifts || []) {
        const charge = Number.isSafeInteger(gift.unitCharge) && gift.unitCharge >= 0
          ? `${this.t('sample_each', { price: this.format(gift.unitCharge) })} · ${this.format(gift.unitCharge * gift.quantity)}`
          : this.t('included');
        const row = node('div', null, 'tm-bb-review-row');
        row.append(node('span', `${this.campaignProvider ? this.t('sample_tile') : `${gift.title} · ${gift.size}`} · ×${gift.quantity}`), node('span', charge));
        container.append(row);
      }
      this.querySelector('[data-subtotal]').textContent = this.format(offer?.subtotal ?? summary.subtotal);
      const campaignPending = Boolean(this.campaignProvider && !Number.isSafeInteger(offer?.estimatedBagTotal));
      this.querySelector('[data-discount]').textContent = campaignPending ? '—' : this.format(offer?.discount || 0);
      const campaignEstimate = Boolean(this.campaignProvider && offer && Number.isSafeInteger(offer.estimatedBagTotal));
      const subtotalLabel = this.querySelector('[data-subtotal-label]');
      if (subtotalLabel) subtotalLabel.textContent = this.t(campaignEstimate ? 'campaign_selected_subtotal' : 'subtotal');
      for (const [rowSelector, amountSelector, amount] of [
        ['[data-campaign-current-bag-row]', '[data-campaign-current-bag]', offer?.cartTotalBefore],
        ['[data-campaign-uplift-row]', '[data-campaign-uplift]', offer?.existingUplift],
        ['[data-campaign-estimated-bag-row]', '[data-campaign-estimated-bag]', offer?.estimatedBagTotal]
      ]) {
        const row = this.querySelector(rowSelector);
        if (row) row.hidden = !campaignEstimate;
        const target = this.querySelector(amountSelector);
        if (target && campaignEstimate) target.textContent = this.format(amount);
      }
      const charge = this.preview ? this.presentation.sampleCharge : (this.prepared || this.campaignEstimate)?.sampleCharge || 0;
      const chargeRow = this.querySelector('[data-sample-charge-row]');
      if (chargeRow) chargeRow.hidden = !(this.preview || this.prepared || this.campaignEstimate) || !charge;
      const chargeAmount = this.querySelector('[data-sample-charge]');
      if (chargeAmount) chargeAmount.textContent = this.format(charge);
      const discountLabel = this.querySelector('[data-discount-label]');
      if (discountLabel) discountLabel.textContent = this.t(campaignEstimate ? 'campaign_selection_savings' : this.preview ? 'fragrance_savings' : 'save');
      if (campaignPending) this.querySelector('[data-review-dialog] [data-total]').textContent = '—';
      const sampleDisclosure = this.querySelector('[data-sample-disclosure]');
      if (sampleDisclosure) sampleDisclosure.hidden = !(this.preview || this.prepared || this.campaignEstimate) || !charge;
      container.parentElement.scrollTop = scroll;
      if (focusedId && focusedAdjust && this.querySelector('[data-review-dialog]').open) container.querySelector(`[data-id="${focusedId}"][data-adjust="${focusedAdjust}"]`)?.focus({ preventScroll: true });
    }
    suppressSticky() { const bar = this.querySelector('[data-sticky]'); if (bar) { bar.hidden = true; bar.inert = true; } }
    syncSticky() {
      const bar = this.querySelector('[data-sticky]'); if (!bar || !this.state) return;
      const rect = this.querySelector('[data-catalog]').getBoundingClientRect();
      const panel = this.querySelector('[data-purchase-area]').getBoundingClientRect();
      const visible = Object.keys(this.draft).length > 0 && rect.top < innerHeight && rect.bottom > innerHeight / 2 && panel.bottom <= 0 && !overlayActive();
      bar.hidden = !visible; bar.inert = !visible;
    }
    clearCampaignReview() {
      this.querySelectorAll('[data-campaign-review],[data-campaign-consent]').forEach(panel => panel.remove());
      this.prepared = null; this.campaignEstimate = null; this.campaignMilestones = null;
      this.recoverySnapshot = null; this.campaignMismatch = false;
      this.campaignProvider = null; this.campaignUnavailable = false;
      this.campaignPanel = this.campaignConsent = this.campaignSummary = this.campaignChoice = this.campaignConfirm = this.campaignActual = this.campaignResolve = null;
    }
    bindCampaign(signal) {
      this.clearCampaignReview();
      try {
        const config = JSON.parse(document.querySelector('[data-tm-bb-campaign-configuration]')?.textContent || '{"enabled":false}');
        this.campaignProvider = window.TheManBundleProviderFactory?.install(config, this);
      } catch (_) { this.campaignUnavailable = true; }
      if (!this.campaignProvider) return;
      const panel = node('section'); panel.dataset.campaignReview = ''; panel.hidden = true;
      const consent = node('div', null, 'tm-bb-campaign-consent'); consent.dataset.campaignConsent = '';
      this.campaignSummary = node('p'); this.campaignSummary.setAttribute('role', 'status');
      const choiceLabel = node('label'); this.campaignChoice = node('input'); this.campaignChoice.type = 'checkbox'; this.campaignChoice.checked = true;
      choiceLabel.append(this.campaignChoice, document.createTextNode(this.t('campaign_samples')));
      this.campaignChoice.addEventListener('change', () => { this.prepared = null; this.refreshQuote(); }, { signal });
      this.campaignConfirm = this.querySelector('[data-review-dialog] [data-transfer]');
      this.campaignActual = node('div'); this.campaignActual.dataset.campaignActual = '';
      this.campaignResolve = node('button', this.t('campaign_resolve')); this.campaignResolve.type = 'button'; this.campaignResolve.dataset.campaignResolve = ''; this.campaignResolve.hidden = true;
      this.campaignResolve.addEventListener('click', () => this.resolveCampaign(), { signal });
      consent.append(this.campaignSummary, choiceLabel);
      this.querySelector('[data-review-dialog] .tm-bb-dialog-foot').prepend(consent); this.campaignConsent = consent;
      panel.append(this.campaignActual, this.campaignResolve);
      this.querySelector('[data-review-dialog] .tm-bb-dialog-body').append(panel); this.campaignPanel = panel;
      this.addEventListener('click', event => { if (event.target.closest('[data-cart-recheck]')) this.reviewCampaign(); }, { signal });
      const update = event => {
        this.prepared = null;
        try { this.uncertain = !!this.campaignProvider.pending(); } catch (_) { this.uncertain = true; }
        if (event?.detail?.cart) this.showCampaignCart(event.detail);
        this.querySelectorAll('[data-cart-recheck]').forEach(button => { button.hidden = !this.uncertain && !this.campaignMismatch; });
        this.refreshQuote();
      };
      window.addEventListener('storage', update, { signal });
      document.addEventListener('tm:cart-reconciled', update, { signal });
      update();
    }
    showCampaignCart(result) {
      this.campaignPanel.hidden = false; this.campaignActual.replaceChildren();
      this.campaignActual.append(node('p', this.t('campaign_actual')));
      for (const line of result.cart?.items || []) this.campaignActual.append(node('p', `${line.product_title || line.title || line.variant_id} × ${line.quantity} · ${this.format(line.final_line_price)}`));
      if (result.cart) this.campaignActual.append(node('strong', `${this.t('campaign_bag_total')} ${this.format(result.cart.total_price)}`));
      this.campaignMismatch = !!result.mismatches?.length;
      if (this.campaignMismatch) this.campaignActual.append(node('p', this.t('campaign_mismatch')));
      this.querySelectorAll('[data-cart-recheck]').forEach(button => { button.hidden = false; });
    }
    campaignVersionMatches() {
      try {
        const config = JSON.parse(document.querySelector('[data-tm-bb-campaign-configuration]').textContent);
        return config.enabled === true && config.id === this.campaignProvider.campaignId && config.revision === this.campaignProvider.revision;
      } catch (_) { return false; }
    }
    async prepareCampaign() {
      if (this.pending || this.uncertain) return;
      const lifecycle = this.controller;
      this.pending = true; this.prepared = null; this.campaignEstimate = null;
      this.quoteSequence = (this.quoteSequence || 0) + 1;
      this.render();
      try {
        const lines = this.state.selection(this.draft, this.catalog).map(({ id, quantity }) => ({ id, quantity }));
        if (!this.campaignVersionMatches()) throw new Error('CAMPAIGN_CONFIGURATION_CHANGED');
        const includeSamples = this.campaignChoice.checked;
        const prepared = await this.campaignProvider.prepare({ lines, currency: this.dataset.currency, includeSamples });
        if (!this.isConnected || this.controller !== lifecycle) return;
        if (includeSamples !== this.campaignChoice.checked || !this.campaignVersionMatches()) throw new Error('CAMPAIGN_REVIEW_CHANGED');
        this.prepared = prepared;
        for (const [id, item] of Object.entries(prepared.consent.merchandise || {})) { if (this.catalog.has(id)) Object.assign(this.catalog.get(id), item); }
        this.campaignPanel.hidden = true; this.campaignResolve.hidden = true;
        this.campaignSummary.textContent = prepared.sampleQuantity
          ? this.t('campaign_disclosure', { quantity: prepared.sampleQuantity, price: this.format(prepared.sampleCharge) })
          : this.t('campaign_no_samples');
        this.status(this.t('campaign_estimated')); this.renderReview();
        this.openDialog(this.querySelector('[data-review-dialog]'), document.activeElement);
      } catch (error) {
        if (this.controller !== lifecycle) return;
        this.uncertain = !!error.uncertain;
        if (error.cart) this.showCampaignCart(error);
        this.status(this.t(error.uncertain && error.message === 'FULL_BAG_ESTIMATE_UNAVAILABLE'
          ? 'campaign_total_unavailable' : error.uncertain ? 'uncertain' : 'campaign_changed'));
        this.querySelectorAll('[data-cart-recheck]').forEach(button => { button.hidden = !this.uncertain; });
      } finally { if (this.controller === lifecycle) { this.pending = false; this.render(); } }
    }
    async confirmCampaign() {
      if (this.pending || this.uncertain || !this.prepared) return;
      const lifecycle = this.controller;
      const prepared = this.prepared, opener = document.activeElement;
      this.pending = true; this.campaignConfirm.disabled = true; this.render();
      try {
        if (!this.campaignVersionMatches()) throw new Error('CAMPAIGN_CONFIGURATION_CHANGED');
        const lines = this.state.selection(this.draft, this.catalog).map(({ id, quantity }) => ({ id, quantity }));
        if (this.campaignChoice.checked !== prepared.consent.includeSamples || this.state.key(lines) !== this.state.key(prepared.consent.selection)) throw new Error('CAMPAIGN_REVIEW_CHANGED');
        const result = await this.campaignProvider.transfer(prepared);
        if (this.controller !== lifecycle) return;
        this.prepared = null; this.campaignEstimate = null;
        this.quoteSequence = (this.quoteSequence || 0) + 1;
        this.showCampaignCart(result);
        this.uncertain = !!result.uncertain || !!this.campaignProvider.pending();
        this.status(this.t(result.offersReconciled ? 'added_bag' : 'campaign_mismatch'));
        if (result.offersReconciled) {
          this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close()); this.suppressSticky();
          await window.TheManPaidCart.open(opener);
        }
      } catch (error) {
        if (this.controller !== lifecycle) return;
        this.prepared = null; this.campaignEstimate = null;
        this.quoteSequence = (this.quoteSequence || 0) + 1;
        try { this.uncertain = !!this.campaignProvider.pending(); } catch (_) { this.uncertain = true; }
        this.status(this.t(this.uncertain ? 'uncertain' : 'campaign_changed'));
        this.querySelectorAll('[data-cart-recheck]').forEach(button => { button.hidden = !this.uncertain; });
      } finally { if (this.controller === lifecycle) { this.pending = false; this.campaignConfirm.disabled = false; this.render(); } }
    }
    async reviewCampaign() {
      if (this.pending) return;
      const lifecycle = this.controller;
      this.pending = true; this.prepared = null; this.recoverySnapshot = null; this.campaignResolve.hidden = true; this.render();
      try {
        try { await this.campaignProvider.recover(); } catch (_) {}
        if (this.controller !== lifecycle) return;
        const reviewed = await this.campaignProvider.reviewRecovery();
        if (this.controller !== lifecycle) return;
        this.recoverySnapshot = reviewed.snapshot; this.showCampaignCart(reviewed);
        this.uncertain = !!this.campaignProvider.pending(); this.campaignResolve.hidden = false;
        this.openDialog(this.querySelector('[data-review-dialog]'), document.activeElement);
        this.status(this.t('campaign_reviewed'));
      } catch (_) { if (this.controller === lifecycle) { this.uncertain = true; this.status(this.t('recovery_failed')); } }
      finally { if (this.controller === lifecycle) { this.pending = false; this.render(); } }
    }
    async resolveCampaign() {
      if (this.pending || !this.recoverySnapshot) return;
      const lifecycle = this.controller;
      this.pending = true; this.campaignResolve.disabled = true;
      try {
        if (!this.campaignVersionMatches()) throw new Error('CAMPAIGN_CONFIGURATION_CHANGED');
        const result = await this.campaignProvider.resolveReviewed(this.recoverySnapshot);
        if (this.controller !== lifecycle) return;
        this.showCampaignCart(result); this.recoverySnapshot = null; this.uncertain = false;
        this.campaignResolve.hidden = true; this.status(this.t('recovery_done'));
      } catch (_) { if (this.controller === lifecycle) { this.recoverySnapshot = null; this.campaignResolve.hidden = true; this.status(this.t('campaign_changed')); } }
      finally { if (this.controller === lifecycle) { this.pending = false; this.campaignResolve.disabled = false; this.render(); } }
    }
    async refreshQuote() {
      clearTimeout(this.quoteTimer);
      const sequence = this.quoteSequence = (this.quoteSequence || 0) + 1;
      if (this.preview) {
        this.quote = null; this.nativePaid = false;
        this.status(this.t('preview_notice')); this.render(); return;
      }
      if (this.campaignProvider) {
        this.prepared = null; this.campaignEstimate = null; this.campaignMilestones = null;
        this.quote = null; this.nativePaid = false;
        this.campaignSummary.textContent = this.t('campaign_estimated');
        this.status(this.t(this.uncertain ? 'uncertain' : 'campaign_estimated')); this.render();
        if (this.uncertain || !this.campaignVersionMatches()) return;
        try { this.campaignMilestones = this.campaignProvider.milestones(); }
        catch (_) { this.status(this.t('unavailable')); this.render(); return; }
        this.render();
        if (Number.isSafeInteger(this.campaignProvider.activeUntil) && this.campaignProvider.activeUntil > Date.now())
          this.quoteTimer = setTimeout(() => this.refreshQuote(), Math.min(this.campaignProvider.activeUntil - Date.now(), 2147483647));
        const lines = this.state.selection(this.draft, this.catalog).map(({ id, quantity }) => ({ id, quantity }));
        if (!lines.length) {
          try {
            const milestones = await this.campaignProvider.previewMilestones(this.campaignChoice.checked);
            if (sequence === this.quoteSequence && this.isConnected && this.campaignVersionMatches() &&
                this.campaignProvider.isActiveHere() && !this.state.selection(this.draft, this.catalog).length) {
              this.campaignMilestones = milestones; this.render();
            }
          } catch (_) {}
          return;
        }
        const selectionKey = this.state.key(lines), includeSamples = this.campaignChoice.checked, lifecycle = this.controller;
        try {
          const estimate = await this.campaignProvider.prepare({ lines, currency: this.dataset.currency, includeSamples });
          if (sequence !== this.quoteSequence || this.controller !== lifecycle || !this.isConnected ||
              this.state.key(this.state.selection(this.draft, this.catalog).map(({ id, quantity }) => ({ id, quantity }))) !== selectionKey ||
              this.campaignChoice.checked !== includeSamples || !this.campaignVersionMatches()) return;
          this.campaignEstimate = estimate; this.render();
        } catch (_) {
          if (sequence === this.quoteSequence && this.controller === lifecycle) {
            this.campaignEstimate = null; this.status(this.t('unavailable')); this.render();
          }
        }
        return;
      }
      if (this.campaignUnavailable) { this.nativePaid = false; this.quote = null; this.status(this.t('unavailable')); this.render(); return; }
      const provider = window.TheManBundleCommerce;
      this.quote = null;
      this.nativePaid = !provider && !!window.TheManPaidCart;
      this.status(this.t(this.uncertain ? 'uncertain' : this.nativePaid ? 'native_paid' : 'unavailable'));
      this.render();
      if (!provider || provider.version !== 1 || typeof provider.quote !== 'function' || typeof provider.transfer !== 'function') return;
      const lines = this.state.selection(this.draft, this.catalog).map(({ id, quantity }) => ({ id, quantity }));
      try {
        const response = await provider.quote({ surface: 'BUILD_YOUR_BUNDLE', lines, currency: this.dataset.currency });
        if (sequence !== this.quoteSequence || !this.isConnected) return;
        const quote = this.state.validateQuote(response, lines, this.dataset.currency);
        if (!quote || quote.subtotal !== this.state.summary(this.draft, this.catalog).subtotal) throw new Error('INVALID_QUOTE');
        const changed = this.lastRevision && this.lastRevision !== quote.revision;
        this.quote = quote; this.lastRevision = quote.revision;
        this.status(this.t(changed ? 'offer_changed' : 'repeat_notice'));
        this.render();
        this.quoteTimer = setTimeout(() => this.refreshQuote(), Math.min(quote.expiresAt - Date.now(), 2147483647));
      } catch (_) {
        if (sequence !== this.quoteSequence || !this.isConnected) return;
        this.quote = null; this.status(this.t('unavailable')); this.render();
      }
    }
    async transfer() {
      if (localPreview()) { this.status(this.t('preview_notice')); return; }
      if (this.campaignProvider) return this.prepared && this.querySelector('[data-review-dialog]').open ? this.confirmCampaign() : this.prepareCampaign();
      if (this.pending || this.uncertain || !(this.nativePaid || this.quote?.purchasable)) return;
      const opener = document.activeElement;
      const approvedRevision = this.quote?.revision;
      this.pending = true; this.render();
      try {
        await this.refreshQuote();
        if (!this.nativePaid && (!this.quote?.purchasable || this.quote.revision !== approvedRevision)) return;
        const lines = this.state.selection(this.draft, this.catalog).map(({ id, quantity }) => ({ id, quantity }));
        const result = this.nativePaid
          ? await window.TheManPaidCart.transfer({ lines, currency: this.dataset.currency })
          : await window.TheManBundleCommerce.transfer({ surface: 'BUILD_YOUR_BUNDLE', lines, quote: this.quote });
        if (!result?.confirmed || !result.before || !result.cart || (!this.nativePaid && !result.offersReconciled) || result.cart.currency !== this.dataset.currency) throw new Error('UNCONFIRMED');
        const delta = this.state.cartDelta(result.before, result.cart, lines);
        if (delta.some(line => line.added !== line.requested)) throw new Error('DELTA_MISMATCH');
        this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
        this.suppressSticky();
        if (this.nativePaid) this.lastAddedIds = lines.map(line => line.id);
        this.status(this.t('added_bag'));
        await window.TheManPaidCart.open(opener);
      } catch (error) { this.uncertain = !!error.uncertain; this.status(error.rejected ? error.message : window.TheManPaidCart.status().message || this.t('uncertain')); }
      finally { this.pending = false; this.render(); }
    }
  }
  class ReadySets extends DialogSurface {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.bindDialogs(signal);
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.cards = [...this.querySelectorAll('[data-set-id]')];
      this.cards.forEach(card => { card.product = JSON.parse(card.querySelector('[data-set-json]').textContent); });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-set-add]');
        if (button) this.transferSet(button.closest('[data-set-id]'));
      }, { signal });
      document.addEventListener('tm:offers-ready', () => this.refreshSets(), { signal });
      const cartUpdated = event => {
        if (!Array.isArray(event.detail?.cart?.items)) return;
        this.cards.forEach(card => {
          if (!card.lastAddedId || cartHasVariants(event.detail.cart, [card.lastAddedId])) return;
          card.lastAddedId = null;
          if (!card.uncertain) this.setStatus(card, '');
        });
      };
      document.addEventListener('cart:updated', cartUpdated, { signal });
      document.addEventListener('tm:cart-state-updated', cartUpdated, { signal });
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refreshSets(); }, { signal });
      window.addEventListener('focus', () => this.refreshSets(), { signal });
      this.refreshSets();
      this.bindRecovery(signal);
    }
    setStatus(card, key) { card.querySelectorAll('[data-set-status]').forEach(el => { el.textContent = this.labels[key] || ''; }); }
    async refreshSet(card) {
      if (localPreview()) {
        card.quote = null; card.nativePaid = false;
        card.querySelectorAll('[data-set-add]').forEach(button => { button.disabled = true; });
        this.setStatus(card, 'preview_notice'); return;
      }
      const provider = window.TheManBundleCommerce;
      const sequence = card.sequence = (card.sequence || 0) + 1;
      card.quote = null;
      clearTimeout(card.expiryTimer);
      card.querySelectorAll('[data-set-add]').forEach(button => { button.disabled = true; });
      const variant = card.product.variants.find(v => v.id === card.dataset.setId);
      card.nativePaid = !provider && !!window.TheManPaidCart && variant?.available && card.product.variants.length === 1;
      if (card.nativePaid) {
        card.querySelectorAll('[data-set-add]').forEach(button => { button.disabled = !!this.pending || !!card.uncertain; });
        this.setStatus(card, card.uncertain ? 'uncertain' : ''); return;
      }
      if (!provider || provider.version !== 1 || !window.TheManBundleState || !variant?.available || card.product.variants.length !== 1) return;
      try {
        const lines = [{ id: variant.id, quantity: 1 }];
        const response = await provider.quote({ surface: 'READY_MADE_SET', lines, currency: this.dataset.currency });
        if (!this.isConnected || sequence !== card.sequence) return;
        const quote = window.TheManBundleState.validateQuote(response, lines, this.dataset.currency);
        if (!quote?.purchasable || quote.subtotal !== variant.price || quote.discount !== 0 || quote.gifts.length || quote.tiers.length) return;
        card.quote = quote;
        card.querySelectorAll('[data-set-add]').forEach(button => { button.disabled = !!this.pending; });
        this.setStatus(card, 'set_repeat_notice');
        card.expiryTimer = setTimeout(() => this.refreshSet(card), Math.max(1, Math.min(quote.expiresAt - Date.now(), 2147483647)));
      } catch (_) { this.setStatus(card, 'unavailable'); }
    }
    refreshSets() { this.cards.forEach(card => this.refreshSet(card)); }
    async transferSet(card) {
      if (localPreview()) { this.setStatus(card, 'preview_notice'); return; }
      if (this.pending || card.uncertain || !(card.nativePaid || card.quote?.purchasable)) return;
      const opener = document.activeElement;
      const revision = card.quote?.revision;
      this.pending = true;
      this.querySelectorAll('[data-set-add]').forEach(button => { button.disabled = true; });
      try {
        await this.refreshSet(card);
        if (!card.nativePaid && (!card.quote || card.quote.revision !== revision)) { this.setStatus(card, 'offer_changed'); return; }
        const lines = [{ id: card.dataset.setId, quantity: 1 }];
        const result = card.nativePaid
          ? await window.TheManPaidCart.transfer({ lines, currency: this.dataset.currency })
          : await window.TheManBundleCommerce.transfer({ surface: 'READY_MADE_SET', lines, quote: card.quote });
        if (!result?.confirmed || !result.before || !result.cart || (!card.nativePaid && !result.offersReconciled) || result.cart.currency !== this.dataset.currency) throw new Error('UNCONFIRMED');
        if (window.TheManBundleState.cartDelta(result.before, result.cart, lines).some(line => line.added !== line.requested)) throw new Error('DELTA_MISMATCH');
        this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
        if (card.nativePaid) card.lastAddedId = lines[0].id;
        this.setStatus(card, 'added_bag');
        await window.TheManPaidCart.open(opener);
      } catch (error) { card.uncertain = !!error.uncertain; card.querySelectorAll('[data-set-status]').forEach(el => { el.textContent = error.rejected ? error.message : window.TheManPaidCart.status().message || this.labels.uncertain; }); }
      finally {
        this.pending = false;
        this.cards.forEach(item => item.querySelectorAll('[data-set-add]').forEach(button => { button.disabled = !!item.uncertain || !(item.nativePaid || (item.quote?.purchasable && item.quote.expiresAt > Date.now())); }));
      }
    }
    disconnectedCallback() {
      this.disconnectDialogs(); this.controller?.abort(); this.controller = null;
      this.cards?.forEach(card => { clearTimeout(card.expiryTimer); card.sequence++; });
    }
  }
  class CampaignFilm extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      const { signal } = this.controller;
      this.button = this.querySelector('[data-video-toggle]');
      this.button.addEventListener('click', () => {
        if (!this.video) return;
        if (this.video.paused) { this.manualPause = false; this.video.play().catch(() => {}); }
        else { this.manualPause = true; this.video.pause(); }
      }, { signal });
      const reset = () => this.mountVideo();
      this.reduced.addEventListener('change', reset, { signal }); this.mobile.addEventListener('change', reset, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) this.video?.pause();
        else if (!this.reduced.matches && !this.manualPause) this.video?.play().catch(() => {});
      });
      this.observer.observe(this); this.mountVideo();
    }
    mountVideo() {
      this.video?.pause();
      const mount = this.querySelector('[data-video-mount]'); mount.replaceChildren();
      const template = this.querySelector(this.mobile.matches ? '[data-video-mobile]' : '[data-video-desktop]');
      this.button.hidden = !template;
      if (!template) { this.video = null; return; }
      mount.append(template.content.cloneNode(true)); this.video = mount.querySelector('video');
      this.video.addEventListener('play', () => { this.button.textContent = this.button.dataset.pause; });
      this.video.addEventListener('pause', () => { this.button.textContent = this.button.dataset.play; });
      this.video.addEventListener('playing', () => { this.video.classList.add('tm-bb-video-playing'); });
      this.video.addEventListener('error', () => { this.video.hidden = true; this.button.hidden = true; });
      this.button.textContent = this.button.dataset.play;
      if (this.visible && !this.reduced.matches && !this.manualPause) this.video.play().catch(() => {});
    }
    disconnectedCallback() { this.video?.pause(); this.observer?.disconnect(); this.controller?.abort(); this.controller = null; }
  }
  customElements.define('tm-bundle-builder', BundleBuilder);
  customElements.define('tm-ready-sets', ReadySets);
  customElements.define('tm-campaign-film', CampaignFilm);
})();
